/**
 * Takes a map of string keys -> values and output them in URL format.
 *
 * This function is used for _nested_ parameters instead of populating
 * a URLSearchParameters object and calling toString() on it due to the
 * fact doing so causes commas to be encoded which is documented as being
 * undesirable in the Google Ad Manager documentation.
 * (https://support.google.com/admanager/answer/1080597)
 */
const toUrlPairs = (innerParams: Map<string, string>) =>
  Array.from(innerParams)
    .map(pair => pair.join('='))
    .join('&');

/**
 * Add params to url.
 * @param  {string} url      Url to append params to.
 * @param  {Map} params      Map of key value pairs.
 */
export function addParams(
  url: string | null | undefined,
  params: Map<string, string | Record<string, string>>
) {
  if (url === undefined || url === null) return undefined;
  try {
    const urlToAppendParams = new URL(url);

    // url.searchParams is not polyfilled in snowpiercer, so we use URLSearchParams constructor instead
    const searchParams = new URLSearchParams(urlToAppendParams.search);

    params.forEach((stringOrObject, paramKey) => {
      if (typeof stringOrObject === 'string') {
        searchParams.set(paramKey, stringOrObject);
      } else if (typeof stringOrObject === 'object' && stringOrObject) {
        const existingParams = new URLSearchParams(searchParams.get(paramKey) ?? '');
        const nestedParams = new Map<string, string>();

        // copy existing params into the nested object
        Array.from(existingParams).forEach(([key, value]) => nestedParams.set(key, value));

        Object.entries(stringOrObject).forEach(([key, value]) => {
          if (typeof value === 'string') {
            // encode string values but explicitly leave reserved characters alone
            nestedParams.set(key, encodeURI(value));
          } else if (!(value === undefined || value === null)) {
            // encode valid structures in their entirety
            nestedParams.set(key, encodeURIComponent(JSON.stringify(value)));
          }
        });

        searchParams.set(paramKey, toUrlPairs(nestedParams));
      }
    });
    urlToAppendParams.search = searchParams.toString();
    return urlToAppendParams.toString();
  } catch (e) {
    return url;
  }
}

/**
 * Add param to url.
 * @param  {string} url        Url to append params to.
 * @param  {string} key        Key as a string.
 * @param  {string} value      The value to append the key.
 */
export function addParam(url: string | undefined, key: string, value: string) {
  return addParams(url, new Map([[key, value]]));
}

export function getParam(url: string | null | undefined, param: string) {
  if (url == null) return undefined;
  const { search } = new URL(url);
  const params = new URLSearchParams(search);
  return params.has(param) ? params.get(param) : undefined;
}
